import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const fontFaceStyle = `
  @font-face {
    font-family: 'Apple Cards Afsal';
    src: url('/fonts/anek.ttf') format('truetype');
  }
`;

// Canvas dimensions (9:16 aspect ratio matching 3240x5760 source)
const CANVAS_W = 810;
const CANVAS_H = 1440;

export function PaymentReceiptModal({ isOpen, onClose, payment }) {
  const receiptRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mlImage, setMlImage] = useState(null);
  const [enImage, setEnImage] = useState(null);
  const [fontLoaded, setFontLoaded] = useState(false);
  const [language, setLanguage] = useState("malayalam");

  // Load both template images
  useEffect(() => {
    const loadImg = (src) =>
      new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = (e) => {
          console.error("Error loading image:", src, e);
          resolve(null);
        };
        img.src = src;
      });

    Promise.all([
      loadImg("/receipt-ml-2026.jpeg"),
      loadImg("/receipt-en-2026.jpeg"),
    ]).then(([ml, en]) => {
      setMlImage(ml);
      setEnImage(en);
    });
  }, []);

  // Load custom font
  const loadCustomFont = async () => {
    const customFont = new FontFace(
      "Apple Cards Afsal",
      "url(/fonts/anek.ttf)",
    );
    try {
      const loadedFont = await customFont.load();
      document.fonts.add(loadedFont);
      setFontLoaded(true);
    } catch (error) {
      console.error("Failed to load font:", error);
    }
  };

  useEffect(() => {
    loadCustomFont();
  }, []);

  if (!payment) return null;

  const currSymbol = payment.currency === "INR" ? "₹" : payment.currency;

  // ---------- Malayalam Receipt (canvas) ----------
  const generateMalayalamReceipt = async () => {
    if (!mlImage || !fontLoaded) {
      alert("Resources still loading. Please try again in a moment.");
      return null;
    }

    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_W * 2;
    canvas.height = CANVAS_H * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);

    // Draw template
    ctx.drawImage(mlImage, 0, 0, CANVAS_W, CANVAS_H);

    ctx.textBaseline = "top";
    ctx.textAlign = "left";

    // --- Name (after "പ്രിയപ്പെട്ട") ---
    ctx.font = 'bold 28px "Apple Cards Afsal"';
    ctx.fillStyle = "#DC2626";
    ctx.fillText(payment.name.toUpperCase(), 112, 272);

    // --- Receipt No. ---
    ctx.font = 'bold 22px "Apple Cards Afsal"';
    ctx.fillStyle = "#DC2626";
    ctx.fillText(payment.paymentId, 240, 562);

    // --- Amount (with stroke for extra thickness) ---
    ctx.font = '900 54px "Apple Cards Afsal"';
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2;
    ctx.strokeText(
      `${currSymbol} ${payment.amount.toLocaleString("en-IN")}/-`,
      210,
      610,
    );
    ctx.fillStyle = "#000000";
    ctx.fillText(
      `${currSymbol} ${payment.amount.toLocaleString("en-IN")}/-`,
      210,
      610,
    );

    // --- Donated by: Name ---
    ctx.font = 'bold 20px "Apple Cards Afsal"';
    ctx.fillStyle = "#1f2937";
    ctx.fillText(payment.name, 150, 778);

    // --- Phone + Place row ---
    ctx.font = 'bold 18px "Apple Cards Afsal"';
    ctx.fillStyle = "#374151";
    // Phone (next to WhatsApp icon)
    ctx.fillText(payment.phone, 150, 812);
    // Place (next to location icon)
    ctx.fillText(payment.place, 370, 812);

    // --- Date ---
    ctx.font = '14px "Arial"';
    ctx.fillStyle = "#666666";
    ctx.textAlign = "left";
    ctx.fillText(
      `Payment Date: ${format(new Date(payment.createdAt), "dd/MM/yyyy HH:mm")}`,
      200,
      680,
    );

    return canvas.toDataURL("image/png");
  };

  // ---------- English Receipt (canvas) ----------
  const generateEnglishReceipt = async () => {
    if (!enImage || !fontLoaded) {
      alert("Resources still loading. Please try again in a moment.");
      return null;
    }

    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_W * 2;
    canvas.height = CANVAS_H * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);

    // Draw template
    ctx.drawImage(enImage, 0, 0, CANVAS_W, CANVAS_H);

    ctx.textBaseline = "top";
    ctx.textAlign = "left";

    // --- Name (after "Dear") ---
    ctx.font = 'bold 28px "Apple Cards Afsal"';
    ctx.fillStyle = "#DC2626";
    ctx.fillText(payment.name.toUpperCase(), 112, 264);

    // --- Receipt No. ---
    ctx.font = 'bold 22px "Apple Cards Afsal"';
    ctx.fillStyle = "#DC2626";
    ctx.fillText(payment.paymentId, 240, 562);

    // --- Amount (with stroke for extra thickness) ---
    ctx.font = '900 54px "Apple Cards Afsal"';
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2;
    ctx.strokeText(
      `${currSymbol} ${payment.amount.toLocaleString("en-IN")}/-`,
      210,
      610,
    );
    ctx.fillStyle = "#000000";
    ctx.fillText(
      `${currSymbol} ${payment.amount.toLocaleString("en-IN")}/-`,
      210,
      610,
    );

    // --- Donated by: Name ---
    ctx.font = 'bold 20px "Apple Cards Afsal"';
    ctx.fillStyle = "#1f2937";
    ctx.fillText(payment.name, 150, 778);

    // --- Phone + Place row ---
    ctx.font = 'bold 18px "Apple Cards Afsal"';
    ctx.fillStyle = "#374151";
    ctx.fillText(payment.phone || "-", 150, 812);
    ctx.fillText(payment.place, 360, 812);

    // --- Date ---
    ctx.font = '14px "Arial"';
    ctx.fillStyle = "#666666";
    ctx.textAlign = "left";
    ctx.fillText(
      `Payment Date: ${format(new Date(payment.createdAt), "dd/MM/yyyy HH:mm")}`,
      200,
      680,
    );

    return canvas.toDataURL("image/png");
  };

  // ---------- Download handler ----------
  const handleDownload = async () => {
    try {
      setIsLoading(true);
      await document.fonts.ready;

      const dataUrl =
        language === "malayalam"
          ? await generateMalayalamReceipt()
          : await generateEnglishReceipt();

      if (!dataUrl) return;

      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

      if (isIOS) {
        const newTab = window.open();
        if (newTab) {
          newTab.document.write(`
            <html>
              <head>
                <title>Receipt - ${payment.name}</title>
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <style>
                  body { margin: 0; padding: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: -apple-system, BlinkMacSystemFont, sans-serif; background-color: #f5f5f5; text-align: center; }
                  img { max-width: 100%; max-height: 85vh; box-shadow: 0 4px 8px rgba(0,0,0,0.1); }
                  .instructions { margin-top: 20px; padding: 10px; background-color: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); max-width: 90%; }
                </style>
              </head>
              <body>
                <img src="${dataUrl}" alt="Receipt"/>
                <div class="instructions"><p>Press and hold on the image, then select "Copy" or "Share"</p></div>
              </body>
            </html>
          `);
          newTab.document.close();
        } else {
          alert(
            'Please long-press on the receipt preview and select "Copy" or "Share"',
          );
        }
      } else {
        const link = document.createElement("a");
        link.download = `payment-receipt-${payment.paymentId}.png`;
        link.href = dataUrl;
        link.click();
      }
    } catch (error) {
      console.error("Error generating receipt:", error);
      alert(
        "An error occurred while generating the receipt. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resourcesReady =
    language === "malayalam" ? mlImage && fontLoaded : enImage && fontLoaded;

  return (
    <>
      <style>{fontFaceStyle}</style>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          className="sm:max-w-[95vw] max-h-[95vh] overflow-y-auto p-4 md:p-6"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <Tabs defaultValue="malayalam" onValueChange={setLanguage}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="malayalam">Malayalam</TabsTrigger>
              <TabsTrigger value="english">English</TabsTrigger>
            </TabsList>

            {/* ===== MALAYALAM TAB ===== */}
            <TabsContent value="malayalam">
              {/* Mobile View */}
              <div className="block sm:hidden">
                <div className="flex flex-col items-center space-y-4">
                  <div className="text-center space-y-2">
                    <h3 className="font-semibold text-lg">Payment Receipt</h3>
                    <p className="text-sm text-muted-foreground">
                      Download receipt for {currSymbol}{" "}
                      {payment.amount.toLocaleString("en-IN")}
                    </p>
                  </div>
                  <div className="w-full max-w-sm space-y-4">
                    <div className="bg-muted p-4 rounded-lg space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">
                          Date
                        </span>
                        <span className="text-sm font-medium">
                          {format(new Date(payment.createdAt), "dd/MM/yyyy")}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">
                          Name
                        </span>
                        <span className="text-sm font-medium">
                          {payment.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">
                          Place
                        </span>
                        <span className="text-sm font-medium">
                          {payment.place}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">
                          Amount
                        </span>
                        <span className="text-sm font-medium">
                          {currSymbol} {payment.amount.toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop View */}
              <div className="hidden sm:block">
                <div className="flex flex-col items-center space-y-4 w-full">
                  <div
                    className="relative"
                    style={{
                      width: `${CANVAS_W}px`,
                      maxWidth: "100%",
                      aspectRatio: `${CANVAS_W}/${CANVAS_H}`,
                    }}
                  >
                    <div
                      ref={language === "malayalam" ? receiptRef : null}
                      className="absolute inset-0 bg-white"
                      style={{
                        width: `${CANVAS_W}px`,
                        height: `${CANVAS_H}px`,
                        position: "relative",
                      }}
                    >
                      {mlImage && (
                        <img
                          src="/receipt-ml-2026.jpeg"
                          alt="Receipt background"
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      )}

                      {/* Name */}
                      <div
                        className="absolute"
                        style={{
                          left: "112px",
                          top: "262px",
                          fontSize: "28px",
                          fontWeight: "bold",
                          color: "#DC2626",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          zIndex: 1,
                          textTransform: "uppercase",
                        }}
                      >
                        {payment.name}
                      </div>

                      {/* Date */}
                      <div
                        className="absolute"
                        style={{
                          left: "200px",
                          top: "672px",
                          fontSize: "14px",
                          color: "#666666",
                          fontFamily: "Arial",
                          zIndex: 1,
                        }}
                      >
                        Payment Date:{" "}
                        {format(new Date(payment.createdAt), "dd/MM/yyyy")}
                      </div>

                      {/* Receipt No */}
                      <div
                        className="absolute"
                        style={{
                          left: "240px",
                          top: "554px",
                          fontSize: "22px",
                          fontWeight: "bold",
                          color: "#DC2626",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          zIndex: 1,
                        }}
                      >
                        {payment.paymentId}
                      </div>

                      {/* Amount */}
                      <div
                        className="absolute"
                        style={{
                          left: "210px",
                          top: "590px",
                          fontSize: "54px",
                          fontWeight: "900",
                          color: "#000000",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          zIndex: 1,
                          textShadow:
                            "1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000",
                        }}
                      >
                        {currSymbol} {payment.amount.toLocaleString("en-IN")}/-
                      </div>

                      {/* Donor Name */}
                      <div
                        className="absolute"
                        style={{
                          left: "150px",
                          top: "770px",
                          fontSize: "20px",
                          color: "#1f2937",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          fontWeight: "bold",
                          zIndex: 1,
                        }}
                      >
                        {payment.name}
                      </div>

                      {/* Phone */}
                      <div
                        className="absolute"
                        style={{
                          left: "150px",
                          top: "805px",
                          fontSize: "18px",
                          color: "#374151",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          fontWeight: "bold",
                          zIndex: 1,
                        }}
                      >
                        {payment.phone}
                      </div>

                      {/* Place */}
                      <div
                        className="absolute"
                        style={{
                          left: "372px",
                          top: "805px",
                          fontSize: "18px",
                          color: "#374151",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          fontWeight: "bold",
                          zIndex: 1,
                        }}
                      >
                        {payment.place}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ===== ENGLISH TAB ===== */}
            <TabsContent value="english">
              {/* Mobile View */}
              <div className="block sm:hidden">
                <div className="flex flex-col items-center space-y-4">
                  <div className="text-center space-y-2">
                    <h3 className="font-semibold text-lg">Payment Receipt</h3>
                    <p className="text-sm text-muted-foreground">
                      Download receipt for {currSymbol}{" "}
                      {payment.amount.toLocaleString("en-IN")}
                    </p>
                  </div>
                  <div className="w-full max-w-sm space-y-4">
                    <div className="bg-muted p-4 rounded-lg space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">
                          Date
                        </span>
                        <span className="text-sm font-medium">
                          {format(new Date(payment.createdAt), "dd/MM/yyyy")}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">
                          Name
                        </span>
                        <span className="text-sm font-medium">
                          {payment.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">
                          Place
                        </span>
                        <span className="text-sm font-medium">
                          {payment.place}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">
                          Amount
                        </span>
                        <span className="text-sm font-medium">
                          {currSymbol} {payment.amount.toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop View */}
              <div className="hidden sm:block">
                <div className="flex flex-col items-center space-y-4 w-full">
                  <div
                    className="relative"
                    style={{
                      width: `${CANVAS_W}px`,
                      maxWidth: "100%",
                      aspectRatio: `${CANVAS_W}/${CANVAS_H}`,
                    }}
                  >
                    <div
                      ref={language === "english" ? receiptRef : null}
                      className="absolute inset-0 bg-white"
                      style={{
                        width: `${CANVAS_W}px`,
                        height: `${CANVAS_H}px`,
                        position: "relative",
                      }}
                    >
                      {enImage && (
                        <img
                          src="/receipt-en-2026.jpeg"
                          alt="Receipt background"
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      )}

                      {/* Name */}
                      <div
                        className="absolute"
                        style={{
                          left: "111px",
                          top: "252px",
                          fontSize: "28px",
                          fontWeight: "bold",
                          color: "#DC2626",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          zIndex: 1,
                          textTransform: "uppercase",
                        }}
                      >
                        {payment.name}
                      </div>

                      {/* Date */}
                      <div
                        className="absolute"
                        style={{
                          left: "200px",
                          top: "672px",
                          fontSize: "14px",
                          color: "#666666",
                          fontFamily: "Arial",
                          zIndex: 1,
                        }}
                      >
                        Payment Date:{" "}
                        {format(new Date(payment.createdAt), "dd/MM/yyyy")}
                      </div>

                      {/* Receipt No */}
                      <div
                        className="absolute"
                        style={{
                          left: "240px",
                          top: "554px",
                          fontSize: "22px",
                          fontWeight: "bold",
                          color: "#DC2626",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          zIndex: 1,
                        }}
                      >
                        {payment.paymentId}
                      </div>

                      {/* Amount */}
                      <div
                        className="absolute"
                        style={{
                          left: "210px",
                          top: "590px",
                          fontSize: "54px",
                          fontWeight: "900",
                          color: "#000000",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          zIndex: 1,
                          textShadow:
                            "1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000",
                        }}
                      >
                        {currSymbol} {payment.amount.toLocaleString("en-IN")}/-
                      </div>

                      {/* Donor Name */}
                      <div
                        className="absolute"
                        style={{
                          left: "150px",
                          top: "770px",
                          fontSize: "20px",
                          color: "#1f2937",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          fontWeight: "bold",
                          zIndex: 1,
                        }}
                      >
                        {payment.name}
                      </div>

                      {/* Phone */}
                      <div
                        className="absolute"
                        style={{
                          left: "150px",
                          top: "805px",
                          fontSize: "18px",
                          color: "#374151",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          fontWeight: "bold",
                          zIndex: 1,
                        }}
                      >
                        {payment.phone || "N/A"}
                      </div>

                      {/* Place */}
                      <div
                        className="absolute"
                        style={{
                          left: "372px",
                          top: "805px",
                          fontSize: "18px",
                          color: "#374151",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          fontWeight: "bold",
                          zIndex: 1,
                        }}
                      >
                        {payment.place}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <Button
            onClick={handleDownload}
            className="mt-4 w-full sm:w-auto"
            disabled={isLoading || !resourcesReady}
          >
            <Download className="mr-2 h-4 w-4" />
            {isLoading
              ? "Generating..."
              : !resourcesReady
                ? "Loading resources..."
                : "Download Receipt"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

PaymentReceiptModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  payment: PropTypes.shape({
    paymentId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    place: PropTypes.string.isRequired,
    amount: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    phone: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
  }).isRequired,
};
